import React from 'react';
import { healthAPI } from '../api';
import { UserProfile } from '../types';
import './OtherInputsMenu.css';

interface OtherInputsMenuProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: () => void;
    profile: UserProfile | null;
}

export const OtherInputsMenu: React.FC<OtherInputsMenuProps> = ({
    isOpen,
    onClose,
    onSubmit,
    profile
}) => {
    if (!isOpen) return null;

    const waterTrackingEnabled = profile?.settings?.water_tracking_enabled ?? true;
    const otherInputs = profile?.settings?.other_inputs || profile?.settings?.medications || [];

    const handleWaterClick = async (amountOz: number) => {
        try {
            const amountMl = Math.round(amountOz * 29.5735); // Convert oz to ml
            await healthAPI.createEvent({
                type: 'water',
                data: {
                    amount_ml: amountMl,
                    amount_oz: amountOz
                }
            });
            onSubmit();
            onClose();
        } catch (error) {
            console.error('Failed to log water:', error);
            alert('Failed to log water intake');
        }
    };

    const handleOtherInputClick = async (item: { name: string; type: string; default_dose?: string }) => {
        try {
            await healthAPI.createEvent({
                type: 'medication',
                data: {
                    name: item.name,
                    type: item.type as 'medication' | 'supplement' | 'other',
                    dose: item.default_dose || ''
                }
            });
            onSubmit();
            onClose();
        } catch (error) {
            console.error('Failed to log other input:', error);
            alert('Failed to log item');
        }
    };

    return (
        <>
            <div className="other-inputs-overlay" onClick={onClose} />
            <div className="other-inputs-menu">
                <div className="menu-header">
                    <h3>Quick Add</h3>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                {waterTrackingEnabled && (
                    <div className="menu-section">
                        <h4>💧 Water</h4>
                        <div className="button-grid">
                            <button
                                className="quick-add-button water"
                                onClick={() => handleWaterClick(8)}
                            >
                                +8oz
                            </button>
                            <button
                                className="quick-add-button water"
                                onClick={() => handleWaterClick(16)}
                            >
                                +16oz
                            </button>
                        </div>
                    </div>
                )}

                {otherInputs.length > 0 && (
                    <div className="menu-section">
                        <h4>📝 Other Inputs</h4>
                        <div className="button-grid">
                            {otherInputs.map((item, idx) => (
                                <button
                                    key={idx}
                                    className="quick-add-button other-input"
                                    onClick={() => handleOtherInputClick(item)}
                                >
                                    {item.name}
                                    {item.default_dose && <span className="dose-label">{item.default_dose}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {!waterTrackingEnabled && otherInputs.length === 0 && (
                    <div className="empty-state">
                        <p>No items configured.</p>
                        <p>Add items in your profile settings.</p>
                    </div>
                )}
            </div>
        </>
    );
};
